import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
import { useState } from "react";
import chiroLogo from "../assets/chiro-logo.png";
import { nl } from "../copy/nl.js";
import { ApiError, fetchAdminBookings, herstelDemo, zetBeheerCode } from "../lib/api.js";

const NAV = [
  { to: "/beheer/kalender", label: nl.beheer.kalender, exact: false },
  { to: "/beheer", label: nl.beheer.boekingen, exact: true },
  { to: "/beheer/afrekeningen", label: nl.beheer.afrekeningen, exact: false },
  { to: "/beheer/tarieven", label: nl.beheer.tarieven.titel, exact: false },
] as const;

/**
 * The admin shell: pine sidebar on desktop, tab bar on a phone (NFR-5 — half
 * of admin work happens standing in the building).
 *
 * No auth in Phase 0. That is a demo-only state and the sidebar says so out
 * loud rather than looking finished.
 */
export function AdminLayout() {
  return (
    <div className="flex min-h-dvh flex-col sm:flex-row">
      {/* flex-wrap: on a narrow phone the nav drops under the logo instead of
          stretching the whole page wider than the viewport (NFR-5). */}
      <aside className="flex flex-wrap items-center justify-between gap-y-1 bg-dennen-diep px-4 py-3 text-kaart sm:min-h-dvh sm:w-52 sm:flex-col sm:items-stretch sm:justify-start sm:px-4 sm:py-5">
        <Link to="/beheer" className="flex items-center gap-2.5">
          {/* A kaart chip under the mark: the red square drowned directly on
              the dark green, and Chiro's own rule is the mark on a light or
              red field, never on an arbitrary colour (user call, 2026-08-21). */}
          <span className="grid place-items-center rounded-lg bg-kaart p-1">
            <img src={chiroLogo} alt={nl.app.logoAlt} className="h-6 w-auto" />
          </span>
          <span>
            <span className="block font-bold font-display text-base leading-tight">
              {nl.app.naam}
            </span>
            <span className="block font-mono text-[#c9d6c8] text-[10px] uppercase tracking-widest">
              {nl.beheer.titel}
            </span>
          </span>
        </Link>
        <nav className="flex gap-1 sm:mt-7 sm:flex-col">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="rounded-lg px-3 py-2 font-medium text-[#c9d6c8] text-sm hover:text-kaart"
              activeProps={{ className: "bg-white/10 font-semibold text-kaart" }}
              activeOptions={{ exact: item.exact }}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="hidden sm:mt-auto sm:block">
          <p className="text-[#7e937e] text-xs leading-relaxed">{nl.beheer.demoNoot}</p>
          <DemoHerstel />
        </div>
      </aside>

      <main className="mx-auto w-full max-w-4xl flex-1 px-5 py-7">
        <CodePoort>
          <Outlet />
        </CodePoort>
      </main>
    </div>
  );
}

/**
 * The demo-reset button (0.20): wipes every change and restores the seed, so
 * the demo survives being passed around a table. Destructive, hence the
 * confirm and the danger styling. The bezig/gelukt/mislukt states are spelled
 * out because a volunteer on 3G stares at them longer than we do. Desktop
 * sidebar only, next to the demo note it belongs to — the phone tab bar has
 * no room for a wipe-everything button.
 */
function DemoHerstel() {
  const queryClient = useQueryClient();
  const herstel = useMutation({
    mutationFn: herstelDemo,
    onSuccess: () => {
      // Every screen is now stale — lists, calendar, details. All of it.
      void queryClient.invalidateQueries();
    },
  });

  return (
    <div className="mt-3">
      <button
        type="button"
        disabled={herstel.isPending}
        onClick={() => {
          if (window.confirm(nl.beheer.demoReset.bevestig)) herstel.mutate();
        }}
        className="min-h-11 w-full rounded-lg border border-rood/70 px-3 font-semibold text-[#f0b3a4] text-xs hover:bg-rood/20 disabled:opacity-50"
      >
        {herstel.isPending ? nl.beheer.demoReset.bezig : nl.beheer.demoReset.knop}
      </button>
      {herstel.isSuccess && (
        <p className="mt-1.5 text-[#c9d6c8] text-xs">{nl.beheer.demoReset.gelukt}</p>
      )}
      {herstel.isError && (
        <p className="mt-1.5 font-semibold text-[#f0b3a4] text-xs">{nl.beheer.demoReset.fout}</p>
      )}
    </div>
  );
}

/**
 * The Phase-0 gate: probes the admin API (the same query every screen shares,
 * so this costs nothing extra) and swaps the whole admin for a code prompt on
 * a 401. Locally the API runs without a code and the gate never shows.
 * F-9 (Google OIDC) replaces this before real data exists.
 */
function CodePoort({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [geprobeerd, setGeprobeerd] = useState(false);
  const probe = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: fetchAdminBookings,
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });

  if (!(probe.error instanceof ApiError && probe.error.status === 401)) return children;

  return (
    <form
      className="mx-auto mt-16 flex max-w-xs flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        zetBeheerCode(code.trim());
        setGeprobeerd(true);
        void queryClient.invalidateQueries();
      }}
    >
      <h1 className="font-bold font-display text-xl tracking-tight">{nl.beheer.login.titel}</h1>
      <p className="text-[13px] text-mist">{nl.beheer.login.uitleg}</p>
      <label className="block">
        <span className="mb-1 block font-semibold text-xs">{nl.beheer.login.veld}</span>
        <input
          type="password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          className="min-h-11 w-full rounded-lg border-2 border-lijn bg-kaart px-3 text-[14px] focus:border-dennen focus:outline-none"
        />
      </label>
      {geprobeerd && !probe.isFetching && (
        <p className="font-semibold text-[12.5px] text-rood">{nl.beheer.login.fout}</p>
      )}
      <button
        type="submit"
        className="min-h-11 rounded-lg bg-rood font-semibold text-kaart text-sm"
      >
        {nl.beheer.login.knop}
      </button>
      <Link to="/" className="text-center text-[12.5px] text-mist hover:text-inkt">
        {nl.beheer.login.terug}
      </Link>
    </form>
  );
}
